import React from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {isValidSocialStreamTab} from '@/utils/validators/socialStream';
import {SocialStreamTab} from '@/types/socialMediaPost';
import {getDefaultCreatorsSort} from '@/views/campaigns/stats/components/creators-stats-table/constants';
import {DEFAULT_SORT as POSTS_DEFAULT_SORT} from '@/views/campaigns/stats/components/organic-posts-table/constants';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import CampaignStatisticsContent from './CampaignStatisticsContent';
import {StatisticsSection} from './statisticsSection';

function getDefaultSocialStream(
  supportsStories: boolean,
  supportsReels: boolean
): SocialStreamTab {
  return supportsStories || supportsReels ? 'all' : 'feed';
}

function isValidStatisticsSection(
  value: string | null
): value is StatisticsSection {
  return value === 'posts' || value === 'creators';
}

function CampaignStatisticsTab() {
  const {campaignId} = useParams<{campaignId: string}>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {campaign} = useCampaignQuery({campaignId});

  const supportsStories = campaign?.socialNetwork?.supportsStories ?? false;
  const supportsReels = campaign?.socialNetwork?.supportsReels ?? false;

  const sectionParam = searchParams.get('section');
  const statisticsSection: StatisticsSection = isValidStatisticsSection(
    sectionParam
  )
    ? sectionParam
    : 'posts';

  const streamParam = searchParams.get('stream');
  const socialStream: SocialStreamTab = isValidSocialStreamTab(streamParam)
    ? streamParam
    : getDefaultSocialStream(supportsStories, supportsReels);

  const handleTabChange = (stream: SocialStreamTab) => {
    searchParams.set('section', 'posts');
    searchParams.set('stream', stream);
    if (stream !== 'feed' && stream !== 'reels') {
      searchParams.delete('start_date');
      searchParams.delete('end_date');
    }
    setSearchParams(searchParams);
  };

  const hasClickTracking = (campaign?.campaignTrackingUrls?.length ?? 0) > 0;

  const handleSectionChange = (section: StatisticsSection) => {
    searchParams.set('section', section);
    searchParams.set(
      'sort',
      section === 'creators'
        ? getDefaultCreatorsSort(hasClickTracking)
        : POSTS_DEFAULT_SORT
    );
    searchParams.delete('q');
    setSearchParams(searchParams);
  };

  return (
    <CampaignStatisticsContent
      campaignId={campaignId!}
      campaign={campaign}
      network={campaign?.network}
      supportsStories={supportsStories}
      supportsReels={supportsReels}
      onTabChange={handleTabChange}
      socialStream={socialStream}
      statisticsSection={statisticsSection}
      onSectionChange={handleSectionChange}
    />
  );
}

export default CampaignStatisticsTab;
